import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { Platform } from '@ionic/angular';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet-markers-canvas';
import { EMarkerIcons } from 'src/app/classes/def/app/icons';
import { IStory } from 'src/app/classes/def/core/story';
import { IMarkerContainer } from 'src/app/pages/marketplace/map/map';
import { ICanvasMarkerContainer, MarkerUtils } from 'src/app/services/map/marker-utils';
import { StringUtils } from 'src/app/services/app/utils/string-utils';
import { EMessageTrim } from 'src/app/classes/utils/message-utils';
import { SleepUtils } from 'src/app/services/utils/sleep-utils';
import { EMarkerIconType } from 'src/app/classes/def/map/markers';

export type LatLngTuple = [number, number];

export interface ILeplaceLeafletMarker {
    index?: number;
    lat: number;
    lng: number;
    label: string;
    type: EMarkerIconType;
    customIcon?: string;
    alwaysVisible?: boolean;
    plain?: boolean;
    canvasLabel?: boolean;
    scale?: number;
    data?: any;
    fitBounds?: boolean;
}

@Component({
    selector: 'leplace-map',
    templateUrl: './leplace-map.component.html',
    styleUrls: ['./leplace-map.component.scss'],
})
export class LeplaceMapComponent implements AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef<HTMLDivElement>;

    // Inputs
    @Input() title = 'Map';
    @Input() standalone = false;
    @Input() center: LatLngTuple = [45.75, 21.23];
    @Input() zoom = 12;

    @Input() showSearch = true;
    @Input() showFab = true;
    @Input() fabAction: 'zoomOut' | 'fitMarkers' = 'fitMarkers';

    @Input() tileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    @Input() tileAttribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

    @Input() markers: ILeplaceLeafletMarker[] = [];
    @Input() fitBoundsOnMarkers = true;
    @Input() cluster = true;
    @Input() debounceMs = 250;

    @Input() loading = true;

    @Input() reload;

    @Input() mapClass: string;
    // standalone ? 'map-view-selector-standalone' : 'map-view-selector'

    // Outputs
    @Output() mapReady = new EventEmitter<L.Map>();
    @Output() locationSelected = new EventEmitter<L.LatLng>();
    @Output() zoomChanged = new EventEmitter<number>();
    @Output() moveEnded = new EventEmitter<L.LeafletEvent>();
    @Output() markerClicked = new EventEmitter<ILeplaceLeafletMarker>();
    @Output() searchRequested = new EventEmitter<string>();
    @Output() searchQueryChange = new EventEmitter<string>();

    autocompleteQuery: string = '';
    loaded: boolean = false;

    test: boolean = false;

    private map?: L.Map;
    private tileLayer?: L.TileLayer;
    private markerLayer?: L.LayerGroup;
    private markerClusterGroup?: L.MarkerClusterGroup;
    private markersCanvas?: any;
    private resizeObserver?: ResizeObserver;
    private debounceTimer?: any;

    markPoint: L.Marker;

    useCanvas: boolean = true;
    isRenderingMarkers: boolean = false;

    constructor(private platform: Platform) { }

    ngAfterViewInit(): void {
        this.platform.ready().then(() => {
            const waitForContainer = () => {
                if (this.mapContainer?.nativeElement) {
                    this.initMap();
                    setTimeout(() => this.map?.invalidateSize(), 300);
                } else {
                    setTimeout(waitForContainer, 100);
                }
            };
            waitForContainer();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.map) {
            return;
        }
        if (changes['reload']) {
            console.log("reload triggered > reloading map content");
            this.map.setView(this.center, this.zoom);
            this.redrawMarkers();
            return;
        }

        if (changes['markers'] && this.map) {
            console.log("markers changed > redrawing " + this.markers?.length + " markers..");
            this.redrawMarkers();
        }
        if ((changes['center'] || changes['zoom']) && this.map) {
            this.map.setView(this.center, this.zoom);
        }
    }

    ngOnDestroy(): void {
        if (this.map) {
            this.map.off();
            this.map.remove();
            this.map = undefined;
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = undefined;
        }
        clearTimeout(this.debounceTimer);
    }

    // Template handlers
    onSearchInput(ev: CustomEvent) {
        const value = (ev.detail as any).value ?? this.autocompleteQuery;
        this.autocompleteQuery = value;
        this.searchQueryChange.emit(value);
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this.searchRequested.emit(this.autocompleteQuery.trim());
        }, this.debounceMs);
    }

    onFabClick() {
        if (!this.map) return;
        let placeMarkers: ILeplaceLeafletMarker[] = this.markers != null ? this.markers.filter(m => m.type !== EMarkerIconType.user) : [];
        if (this.fabAction === 'fitMarkers' && placeMarkers.length) {
            const bounds = L.latLngBounds(placeMarkers.map(m => [m.lat, m.lng] as [number, number]));
            this.map.fitBounds(bounds.pad(0.1));
        } else {
            this.zoomOut();
        }
    }

    // Public API
    public zoomOut() {
        if (!this.map) return;
        const z = this.map.getZoom();
        this.map.setZoom(Math.max(0, z - 1));
    }

    public invalidateSize() {
        this.map?.invalidateSize();
    }

    // Internal setup
    private initMap() {

        if ((this.cluster && this.markerClusterGroup) && (this.useCanvas && this.markersCanvas)) {
            console.warn('⚠️ Both cluster and canvas are active — disabling canvas for safety');
            this.useCanvas = false;
        }

        this.map = L.map(this.mapContainer.nativeElement, {
            center: this.center,
            zoom: this.zoom,
            zoomControl: true,
            attributionControl: true,
        });

        this.tileLayer = L.tileLayer(this.tileUrl, {
            attribution: this.tileAttribution,
            maxZoom: 19,
            maxNativeZoom: 19,
        }).addTo(this.map);

        // Canvas only when not clustering
        if (!this.cluster && this.useCanvas) {
            this.markersCanvas = new (L as any).MarkersCanvas().addTo(this.map);
        }

        if (this.cluster) {
            this.markerClusterGroup = L.markerClusterGroup({
                iconCreateFunction: cluster => {
                    const count = cluster.getChildCount();
                    const html = `<div class="cluster-icon">${count}</div>`;
                    return L.divIcon({
                        html,
                        className: 'custom-cluster-icon',
                        iconSize: L.point(42, 42),
                    });
                },
                spiderfyOnMaxZoom: true,
                showCoverageOnHover: true,
                zoomToBoundsOnClick: true,
                animate: false,
                animateAddingMarkers: false,
                chunkedLoading: true,

                chunkDelay: 30,       // delay (ms) between chunks // Try chunkDelay: 20–40 and chunkInterval: 100–150 for mobile smoothness.
                chunkInterval: 100,   // how long (ms) a chunk can run
                chunkProgress: null,  // optional progress callback
            });
            this.map.addLayer(this.markerClusterGroup);
        }

        // Layer for always-visible / non-cluster markers (after cluster = higher z-order)
        this.markerLayer = L.layerGroup();
        this.map.addLayer(this.markerLayer);

        // Map events
        this.map.on('load', () => {
            this.loaded = true;
            this.mapReady.emit(this.map!);
            setTimeout(() => this.map?.invalidateSize(), 0);
        });

        this.map.on('click', (e: L.LeafletMouseEvent) => this.locationSelected.emit(e.latlng));
        this.map.on('zoomend', () => this.zoomChanged.emit(this.map!.getZoom()));
        this.map.on('moveend', ev => this.moveEnded.emit(ev));

        if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(() => this.map?.invalidateSize());
            this.resizeObserver.observe(this.mapContainer.nativeElement);
        }

        this.redrawMarkers();
    }

    private async redrawMarkers() {
        if (!this.map) return;

        console.log("redraw markers");

        if (this.isRenderingMarkers) {
            console.log('Skipping redraw — already rendering markers');
            return;
        }
        this.isRenderingMarkers = true;

        try {
            // 1. Fully clear previous markers
            if (this.cluster) {
                if (this.markerClusterGroup) {
                    const countBefore = this.markerClusterGroup.getLayers().length;
                    if (countBefore) console.log(`Clearing ${countBefore} clustered markers...`);
                    this.markerClusterGroup.clearLayers();
                    await SleepUtils.sleep(100);
                }
                if (this.markerLayer) {
                    this.markerLayer.clearLayers(); // clear always-visible markers too
                }
            } else {
                // non-cluster mode
                if (this.useCanvas && this.markersCanvas) {
                    if (this.markersCanvas.clear) {
                        this.markersCanvas.clear();
                    } else {
                        this.map.removeLayer(this.markersCanvas);
                        this.markersCanvas = new (L as any).MarkersCanvas().addTo(this.map);
                    }
                } else if (this.markerLayer) {
                    this.markerLayer.clearLayers();
                }
            }

            if (!this.markers?.length) return;

            // Optional: de-dupe by lat/lng
            const uniqueMarkers = Array.from(
                new Map(this.markers.map(m => [`${m.lat}-${m.lng}-${m.label}`, m])).values()
            );

            // 2. Add fresh markers
            const leafletMarkers: L.Marker[] = [];

            let promiseAdd = [Promise.resolve(true)];
            for (const m of this.markers) {
                // for (const m of uniqueMarkers) {
                // promiseAdd.push(new Promise(async (resolve) => {
                //     const mc = await this.addCanvasMarker(m);
                //     leafletMarkers.push(mc.marker);
                //     resolve(true);
                // }));

                const mc = await this.addCanvasMarker(m);
                await SleepUtils.sleep(1);
                leafletMarkers.push(mc.marker);

                // const mc = this.addMarker(m);        
                // leafletMarkers.push(mc.marker);
            }

            await Promise.all(promiseAdd);

            // 3. Fit bounds
            if (this.fitBoundsOnMarkers && leafletMarkers.length) {
                const bounds = L.latLngBounds(this.markers.filter(m => (m.fitBounds === true) || (m.fitBounds == null)).map(m => new L.LatLng(m.lat, m.lng)));
                this.map.fitBounds(bounds.pad(0.5));
            }

            if (this.cluster && this.markerClusterGroup) {
                console.log('Cluster layer count after redraw:', this.markerClusterGroup.getLayers().length);
            } else if (this.useCanvas && this.markersCanvas) {
                console.log('Canvas marker count after redraw:', this.markersCanvas.getMarkersCount?.() || 'unknown');
            }
        } catch (err) {
            console.error(err);
        } finally {
            this.isRenderingMarkers = false;
        }
    }


    addMarker(m: ILeplaceLeafletMarker) {
        console.log("add marker: ", m);
        let label: string = m.label;
        label = StringUtils.trimName(label, EMessageTrim.markerCaptionCanvas);

        // Icon options
        var iconOptions: L.IconOptions = {
            iconUrl: EMarkerIcons.locationPin,
            iconSize: [50, 50]
        };
        // Creating a custom icon
        var customIcon = L.icon(iconOptions);

        var markerOptions: L.MarkerOptions = {
            title: label,
            icon: customIcon,
            draggable: true
        };
        let marker: L.Marker<any> = L.marker([m.lat, m.lng], markerOptions);
        let mc: IMarkerContainer<IStory> = {
            marker: marker,
            data: m.data
        };

        // Adding a tooltip to display the title above the marker
        let tooltipOptions: L.TooltipOptions = { permanent: true, direction: 'top', offset: [0, -20] };
        if (m.alwaysVisible) {
            tooltipOptions.className = "marker-always-visible";
        }

        marker.on('click', (e: L.LeafletMouseEvent) => {
            this.markerClicked.emit(m);
        });

        // routing: alwaysVisible -> markerLayer, else clustered / fallback
        if (m.alwaysVisible && this.markerLayer) {
            marker.setZIndexOffset(10000); // ensure z-index above clusters
            marker.addTo(this.markerLayer);
        } else if (this.cluster && this.markerClusterGroup) {
            this.markerClusterGroup.addLayer(marker);
        } else if (!this.cluster && this.useCanvas && this.markersCanvas) {
            this.markersCanvas.addMarker(marker);
        } else if (this.markerLayer) {
            marker.addTo(this.markerLayer);
        }
        return mc;
    }


    addCanvasMarker(m: ILeplaceLeafletMarker): Promise<IMarkerContainer<any>> {
        return new Promise(async (resolve) => {
            let label: string = m.label;
            label = StringUtils.trimName(label, EMessageTrim.markerCaptionCanvas);
            try {
                let customIcon: string = m.customIcon != null ? m.customIcon : EMarkerIcons.location;
                let iconSize: number = 120;
                let offset = -120;
                switch (m.type) {
                    case EMarkerIconType.story:
                        customIcon = EMarkerIcons.story;
                        break;
                    case EMarkerIconType.location:
                        customIcon = EMarkerIcons.locationPin;
                        iconSize = 80;
                        offset = -80;
                        break;
                    case EMarkerIconType.arena:
                        customIcon = EMarkerIcons.arena;
                        break;
                    case EMarkerIconType.treasure:
                        customIcon = EMarkerIcons.treasure;
                        break;
                    case EMarkerIconType.user:
                        customIcon = EMarkerIcons.user;
                        break;
                }

                if (m.scale != null) {
                    iconSize *= m.scale;
                    offset *= m.scale;
                }

                let iconUrl: string = await this.loadCanvasMarkerData(customIcon, m.canvasLabel ? label : null, !m.plain);
                console.log("add marker: ", m);
                if (iconUrl != null) {

                    const icon = L.icon({
                        iconUrl: iconUrl,
                        iconSize: [iconSize, iconSize * 0.8],
                        iconAnchor: [iconSize / 2, iconSize],
                        popupAnchor: [0, -iconSize]
                    });

                    const marker: L.Marker<any> = L.marker([m.lat, m.lng], { icon, title: m.label });

                    let mc: IMarkerContainer<IStory> = {
                        marker: marker,
                        data: m.data
                    };

                    if (!m.canvasLabel) {
                        let tooltipOptions: L.TooltipOptions = { permanent: true, direction: 'top', offset: [0, offset] };
                        if (m.alwaysVisible) {
                            tooltipOptions.className = "marker-always-visible";
                        }
                        marker.bindTooltip(label, tooltipOptions);
                    }

                    marker.on('click', (e: L.LeafletMouseEvent) => {
                        this.markerClicked.emit(m);
                    });

                    // routing: alwaysVisible -> markerLayer, else cluster/canvas/fallback
                    if (m.alwaysVisible && this.markerLayer) {
                        marker.setZIndexOffset(10000); // ensure z-index above clusters
                        marker.addTo(this.markerLayer);
                    } else if (this.cluster && this.markerClusterGroup) {
                        this.markerClusterGroup.addLayer(marker);
                    } else if (!this.cluster && this.useCanvas && this.markersCanvas) {
                        this.markersCanvas.addMarker(marker);
                    } else if (this.markerLayer) {
                        marker.addTo(this.markerLayer);
                    }

                    resolve(mc);
                } else {
                    console.warn("could not load icon url for marker");
                    resolve(null);
                }
            } catch (err) {
                console.error(err);
                resolve(null);
            }
        });
    }


    loadCanvasMarkerData(iconUrl: string, label: string, circularFrame: boolean): Promise<string> {
        let promise: Promise<string> = new Promise((resolve) => {
            let canvas = document.createElement('canvas');
            let f: ICanvasMarkerContainer = MarkerUtils.formatCanvasMarkerContainer(null, 120);
            canvas.width = f.fullW;
            canvas.height = f.fullH;
            // MarkerUtils.setDPI(canvas, 300, true);
            MarkerUtils.setDPI(canvas, 120, false);
            let ctx = canvas.getContext('2d');
            let img = new Image();
            img.crossOrigin = "anonymous";
            img.src = iconUrl;

            img.onload = () => {
                // console.log("draw marker faded: ", opts.faded);
                if (circularFrame) {
                    ctx = MarkerUtils.drawMarkerText(ctx, f, img, null, null, null, false, label, null, true, null);
                } else {
                    ctx = MarkerUtils.drawMarkerTextPlain(ctx, f, img, null, null, null, false, label, null, true, null);
                }
                let icon: string = canvas.toDataURL();
                resolve(icon);
            };
            img.onerror = () => {
                resolve(null);
            };
        });
        return promise;
    }


    removeRefefenceMarker() {
        if (this.markPoint != null) {
            this.markPoint.removeFrom(this.map);
            this.markPoint = null;
        }
    }


    addReferenceMarker(lat: number, lng: number, label: string) {
        if (!this.markPoint) {
            // Icon options
            var iconOptions: L.IconOptions = {
                iconUrl: EMarkerIcons.locationPin,
                iconSize: [50, 50]
            };
            // Creating a custom icon
            var customIcon = L.icon(iconOptions);
            var markerOptions: L.MarkerOptions = {
                title: label,
                icon: customIcon,
                draggable: true
            };
            this.markPoint = L.marker([lat, lng], markerOptions);

            // let popupHtml: string = "<h2>" + label + "</h2>" + `
            //   <button class="detach" style="width:100%">Detach</button>
            // `;
            // this.markPoint.bindPopup(popupHtml).on("popupopen", (a) => {
            //   var popUp = a.target.getPopup()
            //   popUp.getElement().querySelector(".detach").addEventListener("click", (_e) => {
            //     this.deselectAutocompleteItems();
            //     this.setAutocompletePlaceSelectedCustomCoords(null, null);
            //     this.selectedPlace.autocompletePlace = null;
            //     this.selectedPlace.placeResult = null;
            //   });
            // });

            this.markPoint.on('dragend', (event: L.DragEndEvent) => {
                var marker: L.Marker = event.target;
                var position: L.LatLng = marker.getLatLng();
                this.setCustomLoc(position.lat, position.lng);
            });

            this.markPoint.on('click', (event: L.LeafletMouseEvent) => {
                var marker: L.Marker = event.target;
                console.log("clicked marker: ", marker);
                this.showPlaceSelector();
            });

            // this.map.addLayer(this.markPoint);

            this.markPoint.addTo(this.map);
            this.map.setView([lat, lng], 13);

            this.map.on('click', (e) => {
                if (e && e.latlng) {
                    this.setCustomLoc(e.latlng.lat, e.latlng.lng);
                }
            });
        } else {
            let coords: L.LatLngExpression = new L.LatLng(lat, lng);
            this.markPoint.setLatLng(coords);
        }
    }

    setCustomLoc(lat: number, lng: number) {
        if (this.map && this.markPoint) {
            this.markPoint.setLatLng(new L.LatLng(lat, lng));
            this.map.panTo(new L.LatLng(lat, lng));
        }
    }

    showPlaceSelector() {

    }

}
